export type CardGateReason = 'checking' | 'lookup_failed' | 'no_card' | 'no_default' | 'default_expired' | null;

// What the gate is blocking: placing an order or registering for a paid event.
export type CardGateSubject = 'order' | 'event';

type ResolveCardGateReasonInput = {
    loading: boolean;
    lookupFailed: boolean;
    hasDefaultCard: boolean;
    hasAnyCard?: boolean;
    defaultCardExpired?: boolean;
};

export const resolveCardGateReason = ({
    loading,
    lookupFailed,
    hasDefaultCard,
    hasAnyCard = false,
    defaultCardExpired = false,
}: ResolveCardGateReasonInput): CardGateReason => {
    if (loading) return 'checking';
    if (lookupFailed) return 'lookup_failed';
    if (!hasDefaultCard && defaultCardExpired) return 'default_expired';
    if (!hasDefaultCard) return hasAnyCard ? 'no_default' : 'no_card';
    return null;
};

export const getCardGateMessage = (reason: Exclude<CardGateReason, null>, subject: CardGateSubject = 'order'): string => {
    if (reason === 'checking') {
        return 'Checking your saved card. This only takes a moment.';
    }
    if (subject === 'event') {
        if (reason === 'lookup_failed') {
            return 'This event has a cost, but we could not verify your saved card. Retry, or open Payments to check your payment details.';
        }
        if (reason === 'default_expired') {
            return 'This event has a cost, and your default card has expired. Add a new card in Payments and set it as the default, then come back to register.';
        }
        if (reason === 'no_default') {
            return 'This event has a cost, and none of your saved cards is set as the default. Set a default card in Payments, then come back to register.';
        }
        return 'This event has a cost, so you cannot register until you add a payment card. Add a card in Payments, then come back to register.';
    }
    if (reason === 'lookup_failed') {
        return 'This order includes paid items, but we could not verify your saved card. Retry, or open Payments to check your payment details.';
    }
    if (reason === 'default_expired') {
        return 'This order includes paid items, and your default card has expired. Add a new card in Payments and set it as the default to place this order.';
    }
    if (reason === 'no_default') {
        return 'This order includes paid items, and none of your saved cards is set as the default. Set a default card in Payments to place this order.';
    }
    return 'This order includes paid items, so it cannot be placed until you add a payment card. Add a card in Payments, then come back to finish your order.';
};

// Label for the button that opens the Payments page from the gate.
export const getCardGateActionLabel = (reason: CardGateReason): string => (
    reason === 'no_default' ? 'Set Default Card' : 'Add Card'
);
